import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { settingsReauth } from "@/lib/api";

/**
 * Password re-authentication before viewing/updating integration secrets.
 * Returns a short-lived (15 min) reauth token via onSuccess.
 */
export default function ReauthModal({ open, onSuccess, onCancel }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      const res = await settingsReauth(password);
      toast.success("Re-authenticated — Settings unlocked for 15 minutes.");
      onSuccess(res.token, res.expires_at);
      setPassword("");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Re-auth failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="settings-reauth-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-full"
                  style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
              <ShieldAlert className="h-4.5 w-4.5" />
            </span>
            <div>
              <DialogTitle>Owner re-authentication</DialogTitle>
              <DialogDescription>
                Confirm your password to view or change integration secrets.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="reauth-password" className="text-xs uppercase tracking-wider" style={{ color: "#0B2545" }}>
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
              <Input
                id="reauth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className="pl-9"
                placeholder="Enter your admin password"
                data-testid="settings-reauth-password"
              />
            </div>
            {error && (
              <p className="text-xs" style={{ color: "#9B1C1C" }} data-testid="settings-reauth-error">
                {error}
              </p>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !password}
              style={{ background: "#0B2545", color: "#fff" }}
              data-testid="settings-reauth-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Unlock Settings
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
